import { getInstanceDefinition } from '../../cards/cardLookup';
import { parseAlwaysActiveTraits } from '../../cards/effectHelpers';
import { computeBaseAttack } from '../../combat/attackMath';
import { computeTotalLight, computeDarknessPenalty } from '../../combat/lightDarkness';
import { isMonsterFightEligible } from '../../dungeon/fightEligibility';
import {
  hasActiveForcedFightObligation, hasAssignedMonsterObligation, getAssignedMonsterInstanceId,
} from '../../state/selectors';

function weaponAttackValue(state, weaponInstanceId) {
  const traits = parseAlwaysActiveTraits(getInstanceDefinition(state, weaponInstanceId).abilityText.alwaysActive);
  return traits.physicalAttack + traits.magicAttack;
}

// Greedily assigns every legally-equippable weapon in hand to some hero
// (Strength >= Weight), one weapon per hero. Since the engine's Total Attack
// Value is a flat sum regardless of *which* hero holds *which* weapon, the
// only thing that matters is maximizing how many (and which) weapons get
// legally equipped at all - assigning the highest-value weapons first to the
// least-Strength hero that can still hold them keeps higher-Strength heroes
// free for heavier weapons that come later in the greedy pass.
export function planEquips(state, playerId) {
  const player = state.players.find((p) => p.id === playerId);
  const heroes = player.hand
    .map((id) => ({ id, def: getInstanceDefinition(state, id) }))
    .filter((c) => c.def.strength !== undefined && c.def.strength !== null)
    .sort((a, b) => a.def.strength - b.def.strength);
  const weapons = player.hand
    .map((id) => ({ id, def: getInstanceDefinition(state, id) }))
    .filter((c) => c.def.traits.includes('Weapon'))
    .sort((a, b) => weaponAttackValue(state, b.id) - weaponAttackValue(state, a.id));

  const usedHeroes = new Set();
  const plan = [];
  for (const weapon of weapons) {
    const hero = heroes.find((h) => !usedHeroes.has(h.id) && h.def.strength >= (weapon.def.weight || 0));
    if (!hero) continue;
    usedHeroes.add(hero.id);
    plan.push({ heroInstanceId: hero.id, weaponInstanceId: weapon.id });
  }
  return plan;
}

// Applies planEquips's assignments directly to player.equippedWeapons - a
// scoring-only simulation, NOT a dispatched action (no EQUIP_WEAPON legality
// checks/phase requirements), so it can be used to evaluate "how strong would
// my hand be if I entered the dungeon and equipped everything" from ANY
// phase, in particular from AWAITING_ACTION when decideTopLevelAction needs
// to compare a dungeon turn's real potential against a village turn's.
// Critical: without this, "can I win" was being evaluated against the
// *unequipped* hand, which systematically undercounts Attack Value by every
// weapon's contribution and made the bot avoid the dungeon far more than it
// needed to (a real bug caught via self-play games that never terminated).
export function simulateEquippedState(state, playerId) {
  const plan = planEquips(state, playerId);
  if (!plan.length) return state;
  // One weapon per hero (usedHeroes above) - `[weaponInstanceId]`, not the
  // bare id, to match the real equippedWeapons shape (heroInstanceId ->
  // weaponInstanceId[], see effectHelpers.js's getWeaponCapacity). This
  // planner doesn't take advantage of Disowned Bloodrager's 2-weapon
  // capacity - a missed AI optimization, not a correctness bug.
  const equippedWeapons = Object.fromEntries(plan.map(({ heroInstanceId, weaponInstanceId }) => [heroInstanceId, [weaponInstanceId]]));
  return {
    ...state,
    players: state.players.map((p) => (p.id === playerId ? { ...p, equippedWeapons } : p)),
  };
}

// Returns { hallRankIndex, monsterDef, won, value } for the best target, or
// null if no monster is present at all. Callers that haven't already applied
// equips (e.g. decideTopLevelAction) should pass an already-equipped state -
// see simulateEquippedState above.
export function pickBestMonsterToFight(state, playerId) {
  const player = state.players.find((p) => p.id === playerId);
  const { physical, magic } = computeBaseAttack(state, playerId);
  const totalLight = computeTotalLight(state, playerId);
  // Root of Corruption, Mind Control: "must enter the dungeon and attack
  // that monster if able" - same short-circuit shape as Troll Skullcracker's
  // obligation just below, but the assigned monster lives in the shared "in
  // front of you" zone (player.inPlayInstanceIds, not dungeonHall/meta.
  // forcedFightObligation) - checked first since dungeonActions.js's
  // chooseMonster gives it priority when both are somehow active at once.
  // hallRankIndex: -1 (see resolveCombat.js) naturally zeroes the darkness
  // math below without any special-casing.
  if (hasAssignedMonsterObligation(state, playerId)) {
    const monsterInstanceId = getAssignedMonsterInstanceId(state, playerId);
    const monsterDef = getInstanceDefinition(state, monsterInstanceId);
    const darknessPenalty = computeDarknessPenalty({
      boardSide: state.meta.boardSide, hallRankIndex: -1, totalLight, hardDarkness: state.meta.hardDarkness,
    });
    const totalAttack = Math.max(0, physical + magic - darknessPenalty);
    return {
      hallRankIndex: -1, monsterDef, won: totalAttack >= (monsterDef.health || 0), value: (monsterDef.xp || 0) + (monsterDef.points || 0), health: monsterDef.health || 0,
    };
  }
  // Caverns of Bane, Troll Skullcracker's Raid obligation
  // (actions/dungeonActions.js's chooseMonster hard-blocks fighting anything
  // else while obligated) - the AI has no real choice here, so short-circuit
  // straight to it rather than scoring every hall monster and potentially
  // picking one chooseMonster would then reject.
  const obligation = hasActiveForcedFightObligation(state, playerId) ? state.meta.forcedFightObligation : null;
  if (obligation) {
    const hallRankIndex = state.dungeonHall.indexOf(obligation.monsterInstanceId);
    const monsterDef = getInstanceDefinition(state, obligation.monsterInstanceId);
    const darknessPenalty = computeDarknessPenalty({
      boardSide: state.meta.boardSide, hallRankIndex, totalLight, hardDarkness: state.meta.hardDarkness,
    });
    const totalAttack = Math.max(0, physical + magic - darknessPenalty);
    return {
      hallRankIndex, monsterDef, won: totalAttack >= (monsterDef.health || 0), value: (monsterDef.xp || 0) + (monsterDef.points || 0), health: monsterDef.health || 0,
    };
  }
  const candidates = [];
  state.dungeonHall.forEach((instanceId, hallRankIndex) => {
    if (!instanceId) return;
    const monsterDef = getInstanceDefinition(state, instanceId);
    if (!isMonsterFightEligible(state, player, monsterDef, hallRankIndex)) return;
    const darknessPenalty = computeDarknessPenalty({
      boardSide: state.meta.boardSide, hallRankIndex, totalLight, hardDarkness: state.meta.hardDarkness,
    });
    const totalAttack = Math.max(0, physical + magic - darknessPenalty);
    const won = totalAttack >= (monsterDef.health || 0);
    const value = (monsterDef.xp || 0) + (monsterDef.points || 0);
    candidates.push({
      hallRankIndex, monsterDef, won, value, health: monsterDef.health || 0,
    });
  });
  if (!candidates.length) return null;

  const winnable = candidates.filter((c) => c.won);
  if (winnable.length) {
    // Prefer the best reward-to-risk: highest value, tie-broken by lowest Health
    // (cheaper/safer to re-verify against future Battle-effect surprises).
    return winnable.sort((a, b) => (b.value - a.value) || (a.health - b.health))[0];
  }
  // Nothing winnable: still must choose one (rulebook - a legal fight is
  // mandatory if any monster is present). Prefer the least bad outcome:
  // avoid Guardians (they never retreat, so a loss wastes nothing extra, but
  // also gains nothing - deprioritize them equally) and pick the weakest
  // Health target, maximizing the odds a slightly-off estimate still wins.
  return candidates.sort((a, b) => a.health - b.health)[0];
}
